import { differenceInCalendarDays, startOfDay } from 'date-fns';

export const ALL_SLOTS = [
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
  '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM',
];

export const CUPCAKE_SLOTS = ALL_SLOTS.filter(
  (slot) => !['9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM'].includes(slot)
);

export const AFTERNOON_SLOTS = ALL_SLOTS.slice(ALL_SLOTS.indexOf('3:00 PM'));

const MORNING_TO_EARLY_AFTERNOON_SLOTS = ALL_SLOTS.slice(0, ALL_SLOTS.indexOf('2:00 PM') + 1);

export const EXTENDED_SLOTS = [...ALL_SLOTS, '5:30 PM', '6:00 PM'];

function slotIndex(slot) {
  return ALL_SLOTS.indexOf(slot);
}

function slotsFrom(startLabel, sourceSlots) {
  const start = slotIndex(startLabel);
  if (start < 0) return sourceSlots;
  return sourceSlots.filter((slot) => slotIndex(slot) >= start);
}

function slotsUpTo(endLabel, sourceSlots) {
  const end = slotIndex(endLabel);
  if (end < 0) return sourceSlots;
  return sourceSlots.filter((slot) => slotIndex(slot) <= end);
}

export function isLateFridayToSunday(now = new Date()) {
  const day = now.getDay();
  if (day === 5 && now.getHours() >= 9) return true;
  return day === 6 || day === 0;
}

export function isOrderPlacedOnMonday(now = new Date()) {
  return now.getDay() === 1;
}

export function isHoliday(date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const holidays = [`${year}-07-04`, `${year}-12-25`, `${year}-01-01`];
  const formatted = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return holidays.includes(formatted);
}

export function hasRestrictedHours(date) {
  if (!date) return false;
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const formatted = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const restrictedDates = [`${year}-12-24`, `${year}-12-26`, `${year}-12-27`, `${year}-12-31`];
  return restrictedDates.includes(formatted);
}

function skipClosedDays(date, summerTimingEnabled) {
  const next = startOfDay(new Date(date));
  while (
    next.getDay() === 0 ||
    (summerTimingEnabled && next.getDay() === 1) ||
    isHoliday(next)
  ) {
    next.setDate(next.getDate() + 1);
  }
  return next;
}

export function getEarliestPickupDate({ summerTimingEnabled = false, now = new Date() } = {}) {
  const tomorrow = startOfDay(new Date(now));
  tomorrow.setDate(now.getDate() + 1);

  const isLateFriday = now.getDay() === 5 && now.getHours() >= 9;
  const isWeekend = now.getDay() === 6 || now.getDay() === 0;

  let earliest;

  if (isLateFriday || isWeekend) {
    const daysUntilMonday = (8 - now.getDay()) % 7;
    earliest = startOfDay(new Date(now));
    earliest.setDate(now.getDate() + daysUntilMonday);
  } else if (tomorrow.getDay() === 0) {
    earliest = startOfDay(new Date(tomorrow));
    earliest.setDate(earliest.getDate() + 1);
  } else {
    earliest = tomorrow;
  }

  if (summerTimingEnabled) {
    earliest = skipClosedDays(earliest, true);
  }

  return earliest;
}

function applySummerWeekdayCaps(slots, date, summerTimingEnabled) {
  if (!summerTimingEnabled || !date) return slots;
  const dow = date.getDay();
  if (dow === 2 || dow === 3) {
    return slotsUpTo('4:00 PM', slots);
  }
  return slots;
}

function getRestrictedHolidaySlots(date, formatted, year, isCupcakeFlow) {
  if (formatted === `${year}-12-24`) {
    return isCupcakeFlow
      ? MORNING_TO_EARLY_AFTERNOON_SLOTS.filter((slot) => !['9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM'].includes(slot))
      : MORNING_TO_EARLY_AFTERNOON_SLOTS;
  }
  if (formatted === `${year}-12-26`) {
    return isCupcakeFlow
      ? EXTENDED_SLOTS.filter((slot) => !['9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM'].includes(slot))
      : EXTENDED_SLOTS;
  }
  if (formatted === `${year}-12-27` || formatted === `${year}-12-31`) {
    return isCupcakeFlow
      ? MORNING_TO_EARLY_AFTERNOON_SLOTS.filter((slot) => !['9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM'].includes(slot))
      : MORNING_TO_EARLY_AFTERNOON_SLOTS;
  }
  return null;
}

export function getTimeSlotsForDate(
  date,
  {
    summerTimingEnabled = false,
    isCupcakeFlow = false,
    earliestPickup,
    now = new Date(),
  } = {}
) {
  const baseDaySlots = isCupcakeFlow ? CUPCAKE_SLOTS : ALL_SLOTS;

  if (!date) return applySummerWeekdayCaps(baseDaySlots, date, summerTimingEnabled);

  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const formatted = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const isEarliest = differenceInCalendarDays(startOfDay(date), startOfDay(earliestPickup)) === 0;
  const isRestricted = hasRestrictedHours(date);

  if (isEarliest && isRestricted) {
    return [];
  }

  let slots;

  if (isEarliest && !isRestricted) {
    if (summerTimingEnabled) {
      if (isOrderPlacedOnMonday(now)) {
        slots = slotsFrom('3:00 PM', baseDaySlots);
      } else if (isLateFridayToSunday(now)) {
        slots = slotsFrom('12:00 PM', baseDaySlots);
      } else {
        slots = isCupcakeFlow ? CUPCAKE_SLOTS : AFTERNOON_SLOTS;
      }
    } else {
      slots = isCupcakeFlow ? CUPCAKE_SLOTS : AFTERNOON_SLOTS;
    }
  } else if (!isEarliest && isRestricted) {
    slots = getRestrictedHolidaySlots(date, formatted, year, isCupcakeFlow) || baseDaySlots;
  } else {
    slots = baseDaySlots;
  }

  return applySummerWeekdayCaps(slots, date, summerTimingEnabled);
}

export function shouldBlockDate(
  date,
  { summerTimingEnabled = false, earliestPickup } = {}
) {
  if (date.getDay() === 0 || isHoliday(date)) return true;
  if (summerTimingEnabled && date.getDay() === 1) return true;

  if (
    differenceInCalendarDays(startOfDay(date), startOfDay(earliestPickup)) === 0 &&
    hasRestrictedHours(date)
  ) {
    return true;
  }

  return false;
}
